import React from 'react'
import { isToday } from 'date-fns'
import {
  useAuth,
  useQuestionnaire,
  useQuestionnaireForm,
  QuestionnaireQuestionKeys,
  useEditQuestionnaire,
  useSendQuestionnaireResult,
} from '@modmd/data'
import { pxToRem } from 'theme'
import { QuestionnaireForm, QuestionnairePageLayout } from 'sharedComponents/Questionnaire'
import { useToaster } from 'utils/useToaster'
import { Loading } from 'components/Loading'
import { PersonalPageLayout } from 'client-portal/components/PageLayout'
import { Dialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import Button from 'components/Button'

export const Questionnaire = () => {
  const auth = useAuth()
  const { setToastMessage } = useToaster()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isRefetching, setIsRefetching] = React.useState(false)
  const questionnaireForm = useQuestionnaireForm()
  const { values, setValues } = questionnaireForm.questionnaireFormik

  const [editQuestionnaire, { loading: isEditQuestionnaireLoading }] = useEditQuestionnaire()
  const {
    data: questionnaireData,
    loading: isQuestionnaireLoading,
    refetch: questionnaireDataRefetch,
  } = useQuestionnaire({
    userId: auth.data.User.id,
    onlyMostRecentSurvey: true,
  })
  const [
    sendQuestionnaire,
    { loading: isSendingQuestionnaireLoading },
  ] = useSendQuestionnaireResult()

  const mostRecentQuestionnaire = React.useMemo(() => {
    const lastQuestionnaireEntry = questionnaireData?.surveys.surveys[0]
    return {
      id: lastQuestionnaireEntry?.id,
      createdAt: new Date(lastQuestionnaireEntry?.createdAt),
      surveyResponses: lastQuestionnaireEntry?.SurveyResponses.map(({ key, value, id }) => ({
        id,
        key,
        value,
      })),
    }
  }, [questionnaireData])

  React.useEffect(() => {
    if (mostRecentQuestionnaire.surveyResponses && isToday(mostRecentQuestionnaire.createdAt)) {
      void setValues({ surveyResponses: mostRecentQuestionnaire.surveyResponses })
    }
  }, [setValues, mostRecentQuestionnaire])

  const handleRefetchQuetionnaire = React.useCallback(async () => {
    setIsRefetching(true)
    await questionnaireDataRefetch()
    setIsRefetching(false)
  }, [questionnaireDataRefetch])

  const isUserCleared = React.useMemo(
    () =>
      !!questionnaireForm.questionnaireFormik.values.surveyResponses.find(
        ({ key }) => QuestionnaireQuestionKeys.TEMPERATURE_IN_FAHRENHEIT === key
      )?.value,
    [questionnaireForm.questionnaireFormik.values.surveyResponses]
  )

  const handleConfirm = React.useCallback(async () => {
    try {
      if (isToday(mostRecentQuestionnaire.createdAt)) {
        if (isUserCleared) {
          setIsDialogOpen(true)
        } else {
          await editQuestionnaire({
            variables: {
              id: mostRecentQuestionnaire.id!,
              inputData: {
                SurveyResponses: values.surveyResponses.map(({ value, id: responseId }) => ({
                  id: responseId!,
                  value,
                })),
              },
            },
          })
          await handleRefetchQuetionnaire()
          setToastMessage('Questionnaire successfully changed!', 'success')
        }
      } else {
        await sendQuestionnaire({
          variables: {
            inputData: {
              userId: auth.data.User.id,
              SurveyResponses: questionnaireForm.questionnaireFormik.values.surveyResponses,
            },
          },
        })
        await handleRefetchQuetionnaire()
        setToastMessage('Questionnaire successfully saved!', 'success')
      }
    } catch {
      // ignore
    }
  }, [
    auth.data.User.id,
    editQuestionnaire,
    handleRefetchQuetionnaire,
    isUserCleared,
    mostRecentQuestionnaire.createdAt,
    mostRecentQuestionnaire.id,
    questionnaireForm.questionnaireFormik.values.surveyResponses,
    sendQuestionnaire,
    setToastMessage,
    values.surveyResponses,
  ])

  return (
    <PersonalPageLayout hasContentPadding={false}>
      <QuestionnairePageLayout>
        <Loading isLoading={isQuestionnaireLoading || isRefetching} isEmpty={false}>
          <QuestionnaireForm
            isDisabled={isUserCleared && isToday(mostRecentQuestionnaire.createdAt)}
            questionnaireForm={questionnaireForm}
            isFetching={isEditQuestionnaireLoading || isSendingQuestionnaireLoading}
            onConfirm={handleConfirm}
          />
        </Loading>
      </QuestionnairePageLayout>
      <Dialog
        maxWidth={pxToRem(500)}
        isOpen={isDialogOpen}
        onDismiss={() => {
          setIsDialogOpen(false)
        }}
      >
        <Card.Content>
          <Box my={pxToRem(30)}>
            <Text>
              You are not possible to provide more survey responses today, because you have been
              already cleared. Thanks for your reponses.
            </Text>
          </Box>
          <Button
            width="100%"
            colorVariant="secondary"
            onClick={() => {
              setIsDialogOpen(false)
            }}
          >
            Confirm
          </Button>
        </Card.Content>
      </Dialog>
    </PersonalPageLayout>
  )
}
